import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
  enableBodyScroll,
} from "body-scroll-lock";
import React, { ReactNode, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useDependentState } from "src/hooks";
import { convertNlToBr } from "src/services/string.service";
import theme from "src/theme";
import styled from "styled-components";

import { Icon, Modal } from "..";

const Container = styled.div`
  // override container width
  &.container {
    width: auto;
  }
  position: absolute;
  top: 50%;
  left: ${theme.margins.marginBase};
  right: ${theme.margins.marginBase};
  transform: translateY(-50%);
  max-height: 85vh;
  overflow: auto;

  padding: ${theme.margins.paddingPageBig};
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.veryLightGrey};
  box-shadow: 0 0 4px ${theme.colors.veryLightGrey};
`;

const StyledIcon = styled(Icon)`
  position: absolute;
  top: 16px;
  right: 16px;
`;

const Title = styled.h2`
  margin: ${theme.margins.marginBase} 0;
  font-family: ${theme.fonts.subtitle};
  font-size: ${theme.fontSizes.big};
  font-weight: normal;
  text-align: center;
`;

const Separator = styled.div`
  background-color: ${theme.colors.black};
  width: 120px;
  height: 1px;
  margin: auto;
  margin-bottom: ${theme.margins.marginBase};
`;

interface IPopin {
  children: ReactNode;
  title?: string;
  isOpen?: boolean;
  onClose?: () => void;
  portalDomNode?: Element;
  background?: string;
  className?: string;
}

export const Popin = ({
  children,
  isOpen,
  onClose,
  title,
  portalDomNode = document.body,
  background,
  className,
}: IPopin): JSX.Element => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useDependentState(
    () => (typeof isOpen === "boolean" ? isOpen : true),
    [isOpen]
  );

  const closePopin = useCallback(() => {
    setOpen(false);
    onClose?.();
  }, [onClose, setOpen]);

  useEffect(() => {
    if (open) {
      disableBodyScroll(wrapperRef.current);
    } else {
      enableBodyScroll(wrapperRef.current);
    }
  }, [open]);

  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    return (): void => {
      if (openRef.current) {
        clearAllBodyScrollLocks();
      }
    };
  }, []);

  return ReactDOM.createPortal(
    <Modal
      open={open}
      onMaskClick={closePopin}
      className={className}
      background={background}
    >
      <Container className="container" ref={wrapperRef}>
        <StyledIcon name="cross" size={24} onClick={closePopin} />

        {title && (
          <>
            <Title>{convertNlToBr(title)}</Title>
            <Separator />
          </>
        )}

        {children}
      </Container>
    </Modal>,
    portalDomNode
  );
};

Popin.Container = Container;
Popin.Title = Title;
Popin.Separator = Separator;
